// src/lib/menage-evenements-bandeau.js
//
// LE BANDEAU « MÉNAGE RETIRÉ » chez l'agent — règles PURES.
//
// Un ménage réassigné ou annulé DISPARAÎT de l'écran de l'agent par la RLS :
// il planifie sa journée le matin, on lui retire un ménage à midi, il se
// déplace pour rien le soir. La base écrit l'événement (trigger,
// 20261126000001) ; ce module décide ce que l'écran en dit.
//
// ⚠️ SOBRE, SANS LE POURQUOI NI LE QUI. L'agent n'arbitre pas — il doit juste
// ne pas se déplacer pour rien. « Qui a décidé » vit chez l'orchestrateur
// (trace) ; ici : le logement, le jour, c'est tout.
//
// Deux bandeaux, deux tons : le RETRAIT est corail (« ne vous déplacez pas
// pour rien ») ; l'AJOUT est ciel (« voilà ce qui est nouveau ») — une bonne
// nouvelle ne doit pas ressembler à une alerte.

const JOUR_MS = 86400000

/** Les événements d'un TYPE à montrer : non acquittés, récents (7 jours). */
function aMontrer(evenements, type, maintenant) {
  const ref = maintenant instanceof Date ? maintenant.getTime() : Date.now()
  return (Array.isArray(evenements) ? evenements : [])
    .filter((e) => e && e.type === type && !e.vu_le)
    .filter((e) => {
      const t = Date.parse(e.cree_le || '')
      // Sans date lisible on MONTRE : rater un retrait coûte un déplacement
      // pour rien, montrer un événement douteux coûte un clic sur OK.
      return !Number.isFinite(t) || ref - t <= 7 * JOUR_MS
    })
    .sort((a, b) => String(a.scheduled_date).localeCompare(String(b.scheduled_date)))
}

export function retraitsAMontrer(evenements, maintenant) {
  return aMontrer(evenements, 'retrait', maintenant)
}

/**
 * ⚠️ UN AJOUT DONT LE RETRAIT SUIT NE SE MONTRE PAS. Un ménage donné à 10 h et
 * repris à 11 h n'est PAS une nouveauté : annoncer « ajouté » puis « retiré »
 * dans le même bandeau ferait deux messages pour un fait qui n'existe plus.
 * Le retrait, lui, se montre toujours — c'est lui qui évite le déplacement.
 */
export function ajoutsAMontrer(evenements, maintenant) {
  const ajouts = aMontrer(evenements, 'ajout', maintenant)
  const retires = new Set(
    (Array.isArray(evenements) ? evenements : [])
      .filter((e) => e && e.type === 'retrait')
      .map((e) => `${e.run_id}:${e.destinataire_email}`)
  )
  return ajouts.filter((e) => !retires.has(`${e.run_id}:${e.destinataire_email}`))
}

/**
 * LES HORAIRES MODIFIÉS — un par ménage, et seulement si le ménage est encore
 * le sien. Dédupliqué par run : trois glissements successifs sur le même bloc
 * font UN message, pas trois. Masqué si un ajout, un retrait OU un déplacement
 * de DATE du même run est là : « ajouté » inclut son heure, « retiré » la rend
 * sans objet, « déplacé » la périme (l'heure de l'ancien jour ne dit plus rien).
 */
export function heuresAMontrer(evenements, maintenant) {
  const heures = aMontrer(evenements, 'heure', maintenant)
  const couverts = new Set(
    (Array.isArray(evenements) ? evenements : [])
      .filter((e) => e && (e.type === 'retrait' || e.type === 'ajout' || e.type === 'date'))
      .map((e) => `${e.run_id}:${e.destinataire_email}`)
  )
  const vus = new Set()
  return heures.filter((e) => {
    const cle = `${e.run_id}:${e.destinataire_email}`
    if (couverts.has(cle) || vus.has(cle)) return false
    vus.add(cle)
    return true
  })
}

/**
 * LES DATES DÉPLACÉES (chantier « le ménage suit sa réservation », 2026-08-14) —
 * « votre ménage a changé de date ». Même hygiène que « heure » : dédupliqué
 * par run (deux mouvements successifs = un message, aligné sur le dernier),
 * masqué par un ajout/retrait du même run. ET un « date » MASQUE les « heure »
 * du même run — l'heure de l'ancien jour ne veut plus rien dire (règle posée
 * aussi dans le trigger, qui n'émet pas « heure » quand la date bouge).
 */
export function datesAMontrer(evenements, maintenant) {
  const dates = aMontrer(evenements, 'date', maintenant)
  const couverts = new Set(
    (Array.isArray(evenements) ? evenements : [])
      .filter((e) => e && (e.type === 'retrait' || e.type === 'ajout'))
      .map((e) => `${e.run_id}:${e.destinataire_email}`)
  )
  const vus = new Set()
  // Dédup par run en gardant le PLUS RÉCENT (le dernier mouvement fait foi).
  return dates
    .slice()
    .sort((a, b) => String(b.cree_le || '').localeCompare(String(a.cree_le || '')))
    .filter((e) => {
      const cle = `${e.run_id}:${e.destinataire_email}`
      if (couverts.has(cle) || vus.has(cle)) return false
      vus.add(cle)
      return true
    })
    .sort((a, b) => String(a.scheduled_date).localeCompare(String(b.scheduled_date)))
}

/**
 * « Depuis votre dernière visite : … » — la phrase compose ce qu'il y a.
 * Un ménage ajouté · un horaire modifié · un ménage déplacé — jamais quatre
 * bandeaux.
 */
export function phraseDerniereVisite({ ajouts = [], heures = [], dates = [] } = {}) {
  const a = ajouts.length
  const h = heures.length
  const d = dates.length
  if (!a && !h && !d) return null
  const morceaux = []
  if (a) morceaux.push(a === 1 ? 'un ménage ajouté' : `${a} ménages ajoutés`)
  if (d) morceaux.push(d === 1 ? 'un ménage déplacé' : `${d} ménages déplacés`)
  if (h) morceaux.push(h === 1 ? 'un horaire modifié' : `${h} horaires modifiés`)
  return `Depuis votre dernière visite : ${morceaux.join(' · ')}.`
}

/** Compat : la phrase des seuls ajouts (bandeau d'avant le type « heure »). */
export function phraseAjouts(ajouts) {
  return phraseDerniereVisite({ ajouts: Array.isArray(ajouts) ? ajouts : [] })
}

/** « ven. 15 août » — ou « aujourd'hui » / « demain », qui situent mieux. */
export function jourDuRetrait(dateIso, maintenant) {
  if (typeof dateIso !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(dateIso)) return null
  const ref = maintenant instanceof Date ? maintenant : new Date()
  const aujourdHui = new Intl.DateTimeFormat('en-CA', { timeZone: 'Europe/Paris' }).format(ref)
  if (dateIso === aujourdHui) return "aujourd'hui"
  const [y, m, d] = aujourdHui.split('-').map(Number)
  const demain = new Date(Date.UTC(y, m - 1, d + 1)).toISOString().slice(0, 10)
  if (dateIso === demain) return 'demain'
  const [dy, dm, dd] = dateIso.split('-').map(Number)
  return new Intl.DateTimeFormat('fr-FR', {
    weekday: 'short', day: 'numeric', month: 'long', timeZone: 'UTC',
  }).format(new Date(Date.UTC(dy, dm - 1, dd)))
}

/**
 * LA PHRASE DU BANDEAU.
 * 1 retrait  → « Un ménage vous a été retiré — il n'est plus à faire. »
 * N retraits → « N ménages vous ont été retirés — ils ne sont plus à faire. »
 * La liste (logement · jour) vient à part, ligne par ligne.
 */
export function phraseBandeau(retraits) {
  const n = Array.isArray(retraits) ? retraits.length : 0
  if (!n) return null
  return n === 1
    ? 'Un ménage vous a été retiré — il n’est plus à faire.'
    : `${n} ménages vous ont été retirés — ils ne sont plus à faire.`
}
